/**
 * Cross-device theme sync (ТЗ §7) — bridge between the local theme model and the profile.
 *
 * The theme is applied from ``localStorage`` *before* first paint (see ``initTheme``), so the
 * UI is never blocked on the network. Once the profile loads, its ``theme`` takes priority
 * (it is the source of truth across devices) and is mirrored back into ``localStorage`` so a
 * later reload paints the right look instantly. Saving a new theme writes both stores.
 */
import { apiClient } from "@shared/api";
import { ME_PATH, useMe, type Me } from "@entities/user";
import { useEffect } from "react";

import { applyTheme, isTheme, saveTheme, type Theme } from "./model";

/** Persist the chosen theme to the user profile (``PATCH /api/me/``). */
export function saveThemeRemote(theme: Theme): Promise<Me> {
  return apiClient.patch<Me>(ME_PATH, { body: { theme } });
}

/**
 * Hydrate the theme from the user's profile once it loads (profile wins over localStorage).
 *
 * Does not block the first render — the persisted local theme is already applied at startup;
 * this only reconciles to the server value when it arrives.
 */
export function useThemeProfileSync(): void {
  const { data } = useMe();
  const profileTheme = data?.theme;
  useEffect(() => {
    if (isTheme(profileTheme)) {
      applyTheme(profileTheme);
      saveTheme(profileTheme);
    }
  }, [profileTheme]);
}
